import React, { Component } from "react";
import CaseStudyTitle from "../Components/CaseStudyTitle";
import InfoCard from "../Components/InfoCard";
import UserCard from "../Components/UserCard";
import ParagraphBorderLeft from "../Components/ParagraphBorderLeft";
import caseStudyCover from "../Assets/img/webbuilder/webbuilder-cover.png";
import builderFinished from "../Assets/img/webbuilder/builder-finished.png";
import experiments from "../Assets/img/webbuilder/Experiments.png";
import oldwb from "../Assets/img/webbuilder/old-wb.png";
import wbMindmap from "../Assets/img/webbuilder/wb-mindmap.png";
import CSAvatar from "../Assets/img/webbuilder/dog-CS.png";
import wdAvatar from "../Assets/img/webbuilder/cat-wd.png";
import roAvatar from "../Assets/img/webbuilder/bear-chef.png";
import wbPrototype from "../Assets/img/webbuilder/wb-prototype.gif";
import BuilderAbout from "../Assets/img/webbuilder/BuilderAbout.png";
import BuilderPage from "../Assets/img/webbuilder/BuilderPage.png";
import BuilderProd from "../Assets/img/webbuilder/BuilderProd.gif";
import wireframes from "../Assets/img/webbuilder/Wireframes.jpg";
import customerJourney from "../Assets/img/webbuilder/CustomerJourney.png";
import maze from "../Assets/img/webbuilder/Maze.png";
import { motion } from "framer-motion";
import ImgLeftContentRight from "../Components/ImgLeftContentRight";

class webBuilder extends Component {
  render() {
    return (
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ y: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.75, ease: "easeOut" }}
      >
        <div class="case-study">
          <CaseStudyTitle
            title="Redesigning a website builder"
            company="Popmenu"
            chips={["Web", "UX research", "User testing", "Prototyping"]}
          />
          <div class="container">
            <div
              class="case-study-hero img-shadow"
              style={{ backgroundImage: "url(" + caseStudyCover + ")" }}
            ></div>
          </div>

          <div className="container-short">
            <ParagraphBorderLeft content="Popmenu is a restaurant technology company focused on providing restaurateurs with an online presence that includes a website, dynamic menu, online ordering, and more. " />

            <p>
              Popmenu was launching a new product tier called Indie that would
              allow small restaurant owners to onboard and build their website
              on their own for a lower monthly subscription. This new offering
              was geared towards restaurant owners that wanted an online
              presence but couldn't afford our Pro tier.
            </p>

            <p>
              The existing website builder was a major pain-point when it came
              to building restaurant websites. So not only would this be a
              product for new Indie clients, it would also replace the web
              builder for existing clients as well, making this a multi-faceted
              project.
            </p>

            <InfoCard
              style="#E8F3DA"
              title="How might we design a website creation experience that empowers users to build, launch, and manage their site independently and efficiently—all in one sitting—without needing support?"
              content=""
            />

            <h4>My role</h4>
            <p>
              I was the sole product designer on this project and worked on the
              project from discovery to release with my product manager and a
              team of engineers.
            </p>

            <p>
              I worked closely with internal stakeholders such as the web design
              team, customer support, onboarding, and sales to understand their
              needs for this new redesign.
            </p>

            <h3 class="mt-10">Identifying users</h3>

            <p>
              The target user for Indie clients was <b>single location</b>{" "}
              restaurant owners. Restaurant owners varied greatly in technical
              skill. Some were more tech savvy, like new generations of owners,
              but those that have been in the business for a long time were less
              so.
            </p>

            <p>
              We would also release this product to <b>existing clients</b>,
              adding to the complexity. Existing clients have an{" "}
              <b>in-house web designer designing their website.</b>
            </p>

            <p>
              In total there were <b>three main types of users</b> for this
              project:{" "}
            </p>
          </div>
          <div className="container-medium">
            <div className="row">
              <div className="col-md-4" style={{ display: "flex" }}>
                <UserCard
                  img={roAvatar}
                  title="Restaurant Owners/Staff"
                  content="Restaurant owners themselves or one of their staff would update the website when needed"
                />
              </div>
              <div className="col-md-4" style={{ display: "flex" }}>
                <UserCard
                  img={CSAvatar}
                  title="Customer Support"
                  content="Existing clients often submitted requests to customer support to add or update the content on their website"
                />
              </div>
              <div className="col-md-4" style={{ display: "flex" }}>
                <UserCard
                  img={wdAvatar}
                  title="Internal Web Designers"
                  content="During onboarding, a team of our web designers would build client sites with the website builder"
                />
              </div>
            </div>
          </div>
          <div className="container-short">
            <p>
              Web designers needed more advanced settings while customer support
              and restaurant staff needed more straight-forward options that
              were easy to understand.
            </p>
          </div>
          <div className="container-short">
            <h3 class="mt-10">Interviewing clients and internal employees</h3>

            <p>
              Since we already had clients using the existing web builder, my PM
              and I talked to them to learn how they felt about updating their
              websites. We interviewed single-location to large multi-location
              restaurants.
            </p>

            <p>
              The feedback wasn’t great. These were the key issues we heard:
            </p>
            <InfoCard
              title="1. The UI was complicated"
              content={["Clients were scared to make any edit on their website through the current builder because the experience was confusing. Options had no explanations or visuals of what they affected."]}
            />
            <InfoCard
              title="2. Basic edits felt impossible"
              content={["Clients contacted customer support for the smallest changes on their website, like swapping out a photo, putting more stress on customer service."]}
            />
            <InfoCard
              title="3. No live preview or editing"
              content={["Experiences like Squarespace or Wix were the standard and expected from clients. They wanted to see their changes in real time on their website."]}
            />

            <InfoCard
              style="#E8F3DA"
              title="Our internal employees' take"
              content={["We also spoke with internal teams like customer support and web designers. Their feedback echoed what we heard from clients: they wanted real-time editing, like Squarespace, and a draft mode. Right now, they had to juggle two tabs—one for editing and one for viewing the site—and every change went live immediately, which made things even harder."]}
            />
          </div>
          <div class="container-short">
            <h3 class="mt-10">Mapping the client journey</h3>
            <p>
              For the Indie product, I mapped out the experience from onboarding
              to publishing their website with a flow chart to first understand
              the user journey.
            </p>
          </div>
          <div className="container-medium">
            <img className="img-shadow" src={wbMindmap} width="100%" />
          </div>
          <div class="container-short">
            <p>
              Whenever clients would try to edit the websites themselves, anger
              ensued. Small edits were difficult to figure out, especially if
              the client wasn't tech savvy. They didn't trust themselves to edit
              their site, so <b>most would rely on customer support</b> to
              change things for them. This caused a heavy reliance on support,
              clogging their queues with website edit tickets.{" "}
              <b>
                We wanted to put the power of editing their site back into their
                hands.
              </b>
            </p>
          </div>

          <div className="container-medium">
            <img className="img-shadow" src={customerJourney} width="100%" />
          </div>

          <div class="container-short">
            <h3 class="mt-10">Exploration</h3>
            <p>
              For the layout, I looked to familiar patterns from popular web
              builders instead of reinventing the wheel. A live preview was a
              must, but due to time and technical constraints,{" "}
              <b>we couldn’t support inline editing</b> like what you we see on
              Squarespace or Wix. So, I went with a sidebar for content editing.
            </p>
          </div>
          {/* <div class="container">
            <ImgLeftContentRight
              img={wireframes}
              content="For the layout, we looked to familiar patterns from popular web
              builders instead of reinventing the wheel. A live preview was a
              must, but due to time constraints, we couldn’t support inline
              editing. So, we went with a sidebar for content editing."
            />
          </div> */}
          <div className="container-medium">
            <img className="img-shadow" src={wireframes} width="100%" />
          </div>
          <div class="container-short">
            <h3 class="mt-10">Prototyping and user testing</h3>
            <p>
              I built clickable prototypes and tested them with both internal
              teams and clients. I used the feedback to refine the designs
              through multiple test-and-iterate cycles.
            </p>
          </div>

          <div className="container-medium">
            <img className="img-shadow" src={wbPrototype} width="100%" />
          </div>
          <div class="container-short">
            <p>
              I ran usability tests via Zoom and Maze.io, making small updates
              based on consistent trends. Below are examples from one of our
              Maze tests.
            </p>
          </div>

          <div class="container-medium">
            <img className="img-shadow" src={maze} width="100%" />
          </div>

          <div class="container-short">
            <h3 class="mt-10">Releasing it slowly</h3>

            <p>
              We first rolled out the new builder to our internal web design
              team for bug testing and feedback.
            </p>

            <p>
              We made small UX improvements after several rounds of feedback and
              launched the Indie product. New Indie clients were slowly
              trickling in with some successfully making a website on their own
              without having to call support.
            </p>
          </div>

          <div className="container-medium">
            <img className="img-shadow" src={BuilderAbout} width="100%" />
          </div>

          <div className="container-short">
            <p>
              Next, we gradually released it to existing clients in beta groups
              to avoid overwhelming support and to catch any remaining issues.
            </p>

            <p>Here were some of the sentiments post-launch:</p>

            <InfoCard
              title="Clients felt more confident"
              content={[" Clients said they felt much more comfortable editing their sites now that they could see changes in real time. Support tickets went down and more clients were able to edit the site on their own."]}
            />
            <InfoCard
              title="Workflow improved for internal staff"
              content={["Previously customer support and web designers had two tabs open: one for the website editor, the other for the live website. They were thrilled that now everything could be done in one view."]}
            />
            <InfoCard
              title="Indie clients published sites on their own"
              content={["While the Indie onboarding experience still needed some improvements, we saw several new Indie clients create their restaurant website on their own."]}
            />

            <h3 class="mt-10">Continuous testing after launch</h3>
            <p>
              After launch, we continued to reach out to clients for feedback.
              With this feedback, I made some UX improvements, such as adding
              the ability to search for pages, enhancements to the code editors,
              and adding more settings.
            </p>

            <p>
              In the end, the Indie product fizzled out. Even if that product
              tier failed, we ended up improving the user experience of building
              websites for everyone, which was a win!
            </p>

            <p></p>
          </div>

          <div className="container-medium">
            <img className="img-shadow" src={BuilderProd} width="100%" />
          </div>

          <div className="container mt-10">
            <div className="row">
              <div className="col-sm-6">
                <div className="text-left">
                  <a href="/case-study/affinity-advisors">
                    ← A marketplace for transacting specialty insurance
                  </a>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="text-right">
                  <a href="/case-study/maive">
                    Creating an AI marketplace from 0-1 →
                  </a>
                </div>
              </div>
            </div>

            <br />
            <br />
          </div>
        </div>
      </motion.div>
    );
  }
}

export default webBuilder;
